/* eslint-disable no-throw-literal */
import React from "react";
import { Button, FormHelperText, Stack, useMediaQuery } from "@mui/material";
import { CustomeDialog as DialogSlide } from "../../../shared";
import { useImmer } from "use-immer";
import InputControl from "../../../shared/components/InputControl";
import SimpleReactValidator from "simple-react-validator";
import { rejectClientAccountRequestApi } from "../api";
import { errorMessage, successMessage } from "../../../utils";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { LoadingButton } from "@mui/lab";

export const ClientAccountRejectionConfirmation = ({
  open,
  handleCloseDialog,
}) => {
  const isLargeScreen = useMediaQuery("(min-width:1024px)");

  const { formValidator, state, handleFormChange, handleFormSubmit } =
    useClientAccountRejectionConfirmation({
      open,
      handleCloseDialog,
    });

  const helperTexts = {
    reason: formValidator.current.message("reason", state.reason, "required"),
  };

  return (
    <DialogSlide
      open={open}
      handleClose={handleCloseDialog}
      PaperProps={{
        sx: {
          width: isLargeScreen ? "648px" : "400px",
          pb: "16px",
          px: "24px",
          pt: "24px",
        },
      }}
      title="Reject"
      content={
        <Stack
          component="form"
          gap={3}
          py={1}
          justifyContent="space-between"
          onSubmit={handleFormSubmit}
        >
          <Stack gap="10px">
            <FormHelperText>
              Why do you want to Reject the Request?
            </FormHelperText>

            <InputControl
              type="textarea"
              name="reason"
              label="Reason"
              placeholder="Reason"
              rows={5}
              value={state.reason}
              onChange={handleFormChange}
              error={Boolean(helperTexts.reason)}
              helperText={helperTexts.reason}
              required
            />
          </Stack>
          <Stack flexDirection="row" gap="12px" justifyContent="end">
            <Button variant="text" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <LoadingButton
              loading={state.isRejectButtonLoading}
              type="submit"
              variant="contained"
            >
              Submit
            </LoadingButton>
          </Stack>
        </Stack>
      }
    />
  );
};

const useClientAccountRejectionConfirmation = ({ open, handleCloseDialog }) => {
  const [, forceUpdate] = React.useState();
  const { requestId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isStoreAdmin = pathname.includes("/store-admin");

  const [state, setState] = useImmer({
    reason: "",
    isRejectButtonLoading: false,
  });

  const formValidator = React.useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
      element: (message) => message,
    })
  );

  const rejectClientAccountRequest = async ({ requestId, payload }) => {
    triggerRejectButtonLoading(true);
    try {
      const response = await rejectClientAccountRequestApi({
        requestId,
        payload,
      });
      const { success, message } = response;
      if (success) {
        successMessage(message);
        handleCloseDialog();
        navigate(isStoreAdmin ? "/store-admin/client-account-v2":"/client-account-v2", { replace: true });
      } else {
        throw {
          response: {
            data: {
              message,
            },
          },
        };
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ??
        "Failed to reject the request";

      errorMessage(message);
    } finally {
      triggerRejectButtonLoading(false);
    }
  };

  const triggerRejectButtonLoading = (loading) => {
    setState((draft) => {
      draft.isRejectButtonLoading = loading;
    });
  };

  const handleFormChange = (event) => {
    const { value } = event.target;
    setState((draft) => {
      draft.reason = value;
    });
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (formValidator.current.allValid()) {
      await rejectClientAccountRequest({
        requestId,
        payload: { reason: state.reason },
      });
    } else {
      formValidator.current.showMessages();
      forceUpdate(0);
    }
  };

  const resetFormState = () => {
    formValidator.current.hideMessages();
    forceUpdate(1);

    setState((draft) => {
      draft.reason = "";
    });
  };

  //   UseEffect to reset selected emails on close the dialog
  React.useEffect(() => {
    return () => {
      resetFormState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return { formValidator, state, handleFormChange, handleFormSubmit };
};
